import { Button } from '@cian/ui-kit';
import { IButtonProps, TButtonTheme } from '@cian/ui-kit/button/types';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { useChats } from 'shared/hooks/useChats';
import { selectDailyrentBookingGeneral } from 'shared/selectors/dailyrentBooking';
import { selectEarlyAccessContactsFlowEnabled } from 'shared/selectors/earlyAccess/selectEarlyAccessContactsFlowEnabled';

import { useEarlyAccessPaywallContext } from '../../../packages/EarlyAccessPaywall';
import {
  EAccessCheckTrackingStatus,
  EStatus,
  ESubscriptionLimitType,
} from '../../../packages/EarlyAccessPaywall/shared/types/context';
import { SendMessageButton } from '../../components/SendMessageButton/SendMessageButton';
import { PopupOwnOfferContainer } from '../../components/popups/own_offer/container';
import { getAgentAvailability, selectOffer, selectUser } from '../../selectors';
import { getAgent } from '../../selectors/newObject/getAgent';
import { selectIsChatsEnabled } from '../../selectors/offerChat';
import { trackBlockedEarlyAccessChatClick } from '../../utils/tracking/earlyAccessTracking';
import { trackSendMessageClick } from '../ChatPopupContainer/tracking';

interface ISendMessageButtonContainerProps {
  title?: string | null;
  ariaLabel?: string;
  size?: IButtonProps['size'];
  theme?: TButtonTheme;
  beforeIcon?: React.JSX.Element;
  noMarginTop?: boolean;
  placeType?: string;
  onChatButtonClick?: () => void;
  tabIndex?: number;
}

export const SendMessageButtonContainer = ({
  size = 'M',
  title = 'Написать',
  theme = 'fill_secondary',
  beforeIcon,
  ariaLabel,
  noMarginTop,
  placeType,
  onChatButtonClick,
  tabIndex,
}: ISendMessageButtonContainerProps) => {
  const offer = useSelector(selectOffer);
  const { telemetry } = useApplicationContext();
  const { handleOpenChat } = useChats();
  const agentAvailability = useSelector(getAgentAvailability);
  const agent = useSelector(getAgent);
  const user = useSelector(selectUser);
  const isChatsEnabled = useSelector(selectIsChatsEnabled);
  const isAgentNotAvailable = agentAvailability && !agentAvailability.available;
  const [isOwnOfferPopupOpen, setOwnOfferPopupOpen] = React.useState(false);
  const { defaultBookingMessage: dailyrentDefaultBookingMessage } = useSelector(selectDailyrentBookingGeneral);

  const { checkContactsAccess, status } = useEarlyAccessPaywallContext();
  const isEarlyAccessContactsFlowEnabled = useSelector(selectEarlyAccessContactsFlowEnabled);

  const type = isAgentNotAvailable ? 'open_message' : undefined;

  const handleEarlyAccessFlow = async () => {
    if (!user?.isAuthenticated) {
      trackBlockedEarlyAccessChatClick({
        offer,
        agent,
        blockType: EAccessCheckTrackingStatus.Unauthorized,
      });
    }

    const { isConfirmed, trackingStatus } = await checkContactsAccess({
      offerId: offer.id,
      limitType: ESubscriptionLimitType.Chat,
    });

    if (isConfirmed) {
      telemetry.increment('offer_card.send_message_click.aside.early_access');
      trackSendMessageClick({ offer, agent, isEarlyAccess: true });

      handleOpenChat({
        type,
        placeType,
        isEarlyAccess: true,
      });

      return;
    }

    if (trackingStatus) {
      trackBlockedEarlyAccessChatClick({
        offer,
        agent,
        blockType: trackingStatus,
      });
    }
  };

  const onClick = async () => {
    if (isEarlyAccessContactsFlowEnabled) {
      await handleEarlyAccessFlow();

      return;
    }

    telemetry.increment('offer_card.send_message_click.aside');
    trackSendMessageClick({ offer, agent });

    if (onChatButtonClick) {
      onChatButtonClick();
    }

    if (user && user.realtyUserId === offer.userId) {
      setOwnOfferPopupOpen(true);

      return;
    }

    handleOpenChat({
      type,
      shouldSendDefaultMessage: !!dailyrentDefaultBookingMessage,
      defaultMessage: dailyrentDefaultBookingMessage,
      placeType,
    });
  };

  const ownOfferPopupClose = () => setOwnOfferPopupOpen(false);

  if (!(isChatsEnabled && !offer.dealRentVersion)) {
    return null;
  }

  return (
    <>
      <PopupOwnOfferContainer isOpen={isOwnOfferPopupOpen} onClose={ownOfferPopupClose} />

      <SendMessageButton noMarginTop={noMarginTop}>
        <Button
          theme={theme}
          size={size}
          onClick={onClick}
          fullWidth
          beforeIcon={beforeIcon}
          aria-label={ariaLabel}
          tabIndex={tabIndex}
          loading={status === EStatus.Loading}
        >
          {title ?? ''}
        </Button>
      </SendMessageButton>
    </>
  );
};
